/**
 * FastAudio API client
 *
 * Single source of truth for all backend communication.
 * All requests use relative /api paths — the Vite dev proxy
 * (or nginx in production) routes them to the correct backend.
 *
 * Handles response-shape differences between the production backend
 * (fastaudio.cc) and the local development backend (localhost:3001).
 *
 * See API.md at the project root for full endpoint documentation.
 */

// ─── Types (JSDoc) ────────────────────────────────────────────────────────────

/**
 * @typedef {Object} MediaInfo
 * @property {string}      title
 * @property {string|null} thumbnail
 * @property {number|null} duration    — seconds; 0 when unknown
 * @property {string|null} [uploader]  — local backend only
 * @property {number|null} [filesize]  — local backend only; bytes
 * @property {Array<{id:string,label:string}>} [formats] — backend-provided media formats
 * @property {Array<{height:number,label:string,filesize:number|null}>} [videoFormats] — actual available video streams
 * @property {string}      [provider]
 */

/**
 * Normalised result returned by `convert()` regardless of which backend
 * responded. Components should only depend on this shape.
 *
 * @typedef {Object} ConvertResult
 * @property {boolean}        success
 * @property {string}         downloadUrl — ready-to-use relative URL for the file
 * @property {string|null}    title
 * @property {MediaInfo|null} mediaInfo
 * @property {string}         format
 * @property {string}         quality
 * @property {string|null}    jobId       — local backend only; null on production
 */

// ─── Helpers ─────────────────────────────────────────────────────────────────

const BASE = '/api'

/**
 * Thin fetch wrapper that rejects on non-2xx responses.
 *
 * @param {string} path
 * @param {RequestInit} [init]
 * @returns {Promise<unknown>}
 */
async function apiFetch(path, init) {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(data?.error ?? `Request failed (HTTP ${res.status})`)
  }
  return data
}

// ─── POST /api/info ───────────────────────────────────────────────────────────

/**
 * Fetch metadata for a media URL without downloading it.
 *
 * Response: { title, thumbnail, duration, uploader, filesize, formats,
 *             videoFormats, provider }. `videoFormats` is the source of truth
 * for video choices; an empty array means the source has no available video.
 *
 * @param {string} url
 * @returns {Promise<MediaInfo>}
 */
export async function getInfo(url) {
  return /** @type {MediaInfo} */ (
    await apiFetch('/info', {
      method: 'POST',
      body: JSON.stringify({ url }),
    })
  )
}

// ─── POST /api/convert ────────────────────────────────────────────────────────

/**
 * Download and convert a media URL. Long-running — resolves when the file is
 * ready (typically 20–60 s). The returned `downloadUrl` is ready to use
 * immediately.
 *
 * Production backend returns:
 *   { success, title, download: "/api/proxy-download?u=...&t=..." }
 *
 * Local dev backend returns:
 *   { jobId, downloadUrl: "/api/jobs/:id/file", mediaInfo, format, quality }
 *
 * Both shapes are normalised into `ConvertResult`.
 *
 * @param {{ url: string, format?: string, quality?: string, provider?: string }} params
 * @returns {Promise<ConvertResult>}
 */
export async function convert(params) {
  const raw = /** @type {Record<string,unknown>} */ (
    await apiFetch('/convert', {
      method: 'POST',
      body: JSON.stringify({
        url:      params.url,
        format:   params.format  ?? 'mp3',
        quality:  params.quality ?? '192',
        provider: params.provider,
      }),
    })
  )

  // Resolve download URL — production uses `download`, local uses `downloadUrl`
  const downloadUrl =
    /** @type {string|undefined} */ (raw.download) ??
    /** @type {string|undefined} */ (raw.downloadUrl) ??
    null

  if (!downloadUrl) {
    throw new Error('Backend did not return a download URL.')
  }

  // Normalise mediaInfo — production only sends title at the top level
  const rawMediaInfo = /** @type {MediaInfo|null|undefined} */ (raw.mediaInfo)
  const title        = /** @type {string|undefined} */ (raw.title) ?? rawMediaInfo?.title ?? null
  const mediaInfo    = rawMediaInfo ?? (title ? { title, thumbnail: null, duration: null } : null)

  return {
    success:     /** @type {boolean} */ (raw.success) ?? true,
    downloadUrl,
    title,
    mediaInfo,
    format:      /** @type {string} */ (raw.format)  ?? params.format  ?? 'mp3',
    quality:     /** @type {string} */ (raw.quality) ?? params.quality ?? '192',
    jobId:       /** @type {string|null} */ (raw.jobId) ?? null,
  }
}

// ─── GET /api/jobs/:id/events  ────────────────────────────────────────────────
// ⚠️  NOT AVAILABLE on the production backend (fastaudio.cc returns 404).
//     This function exists for local development use only.

/**
 * Subscribe to SSE progress events for a job.
 * Returns the EventSource so the caller can close it when done.
 *
 * ⚠️  This endpoint is missing from the production backend. Calling it
 *     against fastaudio.cc will immediately trigger `onError`.
 *
 * @param {string} jobId
 * @param {{
 *   onProgress?: (data: object) => void,
 *   onDone?:     (data: object) => void,
 *   onError?:    (data: object) => void,
 * }} handlers
 * @returns {EventSource}
 */
export function subscribeToJobEvents(jobId, handlers) {
  const src = new EventSource(`${BASE}/jobs/${jobId}/events`)

  if (handlers.onProgress) {
    src.addEventListener('progress', (e) => {
      try { handlers.onProgress(JSON.parse(/** @type {MessageEvent} */ (e).data)) }
      catch { /* ignore parse errors */ }
    })
  }

  if (handlers.onDone) {
    src.addEventListener('done', (e) => {
      try { handlers.onDone(JSON.parse(/** @type {MessageEvent} */ (e).data)) }
      catch { /* ignore parse errors */ }
    })
  }

  src.onerror = () => {
    handlers.onError?.({ error: 'Job events stream unavailable (endpoint missing on production backend).' })
    src.close()
  }

  return src
}

// ─── GET /api/history  ────────────────────────────────────────────────────────
// ⚠️  NOT AVAILABLE on the production backend (fastaudio.cc returns 404).

/**
 * Fetch the last 50 completed downloads.
 *
 * ⚠️  This endpoint is missing from the production backend.
 *     Throws an `ENDPOINT_MISSING` error when called against fastaudio.cc.
 *
 * @returns {Promise<Array<object>>}
 */
export async function getHistory() {
  const res = await fetch(`${BASE}/history`)
  if (res.status === 404) {
    throw Object.assign(
      new Error('GET /api/history is not implemented on the production backend.'),
      { code: 'ENDPOINT_MISSING' },
    )
  }
  const data = await res.json().catch(() => [])
  if (!res.ok) throw new Error(data?.error ?? 'Failed to fetch history.')
  return /** @type {Array<object>} */ (data)
}

// ─── GET /api/jobs/:id  ───────────────────────────────────────────────────────
// ⚠️  NOT AVAILABLE on the production backend.

/**
 * Poll a job's status.
 *
 * ⚠️  This endpoint is missing from the production backend.
 *
 * @param {string} jobId
 * @returns {Promise<object>}
 */
export async function getJob(jobId) {
  return apiFetch(`/jobs/${jobId}`)
}

// ─── DELETE /api/jobs/:id  ────────────────────────────────────────────────────
// ⚠️  NOT AVAILABLE on the production backend.

/**
 * Cancel or delete a job.
 *
 * ⚠️  This endpoint is missing from the production backend.
 *
 * @param {string} jobId
 * @returns {Promise<object>}
 */
export async function cancelJob(jobId) {
  return apiFetch(`/jobs/${jobId}`, { method: 'DELETE' })
}
